import { ControlPointOutlined, DeleteOutlineOutlined } from '@mui/icons-material';
import { Button, Grid, IconButton } from '@mui/material';
import { Box, SxProps, Theme } from '@mui/system';
import {
  ControlledField,
  DateTimePickerInputField,
  LegSchemaType,
  LocationInputField,
  RideLegsSchemaType,
  colors,
  getDateTimeShortFormatWithLocale,
  LegTypeEnum,
  GoogleConf,
} from 'remote/global';

import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

function Dot({ active }: { active: boolean }) {
  return (
    <Box
      sx={{
        position: 'absolute',
        left: -21,
        top: 52,
        width: 10,
        height: 10,
        backgroundColor: active ? colors.uiBlue400 : 'black',
        borderRadius: '50%',
      }}
    />
  );
}

function Line({ active, sx }: { active: boolean; sx?: SxProps<Theme> }) {
  return (
    <Box
      sx={{
        position: 'absolute',
        left: -17,
        top: 70,
        borderWidth: '1px',
        borderStyle: active ? 'dashed' : 'solid',
        borderColor: active ? colors.uiBlue400 : 'black',
        height: 'calc(100% - 25px)',
        ...sx,
      }}
    />
  );
}

function Triangle({ active }: { active: boolean }) {
  return (
    <Box
      sx={{
        position: 'absolute',
        left: -21,
        bottom: 20,
        width: 0,
        height: 0,
        borderLeft: '5px solid transparent',
        borderRight: '5px solid transparent',
        borderTopWidth: '5px',
        borderTopStyle: 'solid',
        borderTopColor: active ? colors.uiBlue400 : 'black',
        marginTop: '10px',
      }}
    />
  );
}
interface RideInfoItemProps {
  leg: LegSchemaType;
  legIndex: number;
  legs: LegSchemaType[];
  form: UseFormReturn<RideLegsSchemaType>;
  googleConfig: GoogleConf;
  isOrderEditAvailable: boolean;
  onRemoveRow?: (legIndex: number) => void;
  onAddRow?: (legIndex: number) => void;
}
interface AddRowButtonProps {
  onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  children: React.ReactNode;
}

function AddRowButton({ children, onClick }: AddRowButtonProps) {
  return (
    <Button variant='text' onClick={onClick} sx={{ p: 0, fontSize: '14px', height: 'auto' }}>
      <ControlPointOutlined sx={{ fontSize: '16px', mr: 0.5 }} />
      {children}
    </Button>
  );
}

interface RemoveRowButtonProps {
  onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  isHidden: boolean;
}

function RemoveRowButton({ isHidden, onClick }: RemoveRowButtonProps) {
  return (
    <Grid item xs={1} sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <IconButton onClick={onClick} sx={{ visibility: isHidden ? 'hidden' : 'unset' }}>
        <DeleteOutlineOutlined />
      </IconButton>
    </Grid>
  );
}

const isLegEmpty = (leg: LegSchemaType) =>
  [LegTypeEnum.EMPTY_LEG, LegTypeEnum.DEPOT_EMPTY_LEG, LegTypeEnum.CONNECTING_EMPTY_LEG].includes(
    leg.type
  );

function RideInfoItem(props: RideInfoItemProps) {
  const tInstance = useTranslation();
  const { t, i18n } = tInstance;
  const { leg, legs, legIndex, form, googleConfig, onRemoveRow, onAddRow, isOrderEditAvailable } =
    props;
  const { control } = form;

  const isFirstLeg = legIndex === 0;
  const isLastLeg = legIndex === legs.length - 1;
  const isPickupDepot = isFirstLeg && isLegEmpty(leg);
  const isDropOffDepot = isLastLeg && isLegEmpty(leg);
  const isDepot = isPickupDepot || isDropOffDepot;
  const showLastLeg = isDropOffDepot || isLastLeg || legs.length <= 1;

  const getLabel = (isEndLocation = false) => {
    const isEmptyLeg = isLegEmpty(leg);
    if (isFirstLeg) {
      if (isEmptyLeg) return t('terms.attributes.pickup.depot');
      if (!isEndLocation) return t('MultiStop.labels.startLocation');
    }

    if (isLastLeg) {
      if (isEmptyLeg) return t('terms.attributes.dropoff.depot');
      if (isEndLocation) return t('MultiStop.labels.multistopEndLocation');
      return t('MultiStop.labels.stopover');
    }

    const filteredClientLegs = legs.filter((l) => l.type === LegTypeEnum.CLIENT_LEG);
    const isFirstClientLeg = filteredClientLegs.indexOf(leg) === 0;

    if (isFirstClientLeg) return t('MultiStop.labels.startLocation');

    return t('MultiStop.labels.stopover');
  };

  const getCurrentLegProgress = (isEndLocation = false) => {
    if (isLastLeg && isEndLocation) {
      return (
        <>
          <Line active={isDepot} sx={{ top: -15 }} />
          <Triangle active={isDepot} />
        </>
      );
    }

    if (isFirstLeg) {
      return (
        <>
          <Dot active={isPickupDepot} />
          <Line active={isDepot} />
        </>
      );
    }

    const isValidLastLeg = isLastLeg && legs.length > 1;
    return (
      <>
        {!isValidLastLeg && <Line active={isDepot} />}
        <Dot active={isPickupDepot} />
      </>
    );
  };

  return (
    <Grid container gap={2}>
      {isFirstLeg && !isPickupDepot && typeof onAddRow === 'function' && (
        <AddRowButton onClick={() => onAddRow(legIndex)}>
          {t('MultiStop.labels.addPickupDepotLocation')}
        </AddRowButton>
      )}
      <Grid container item spacing={1} sx={{ position: 'relative' }}>
        {getCurrentLegProgress()}
        <Grid container item xs={11} rowGap={1} spacing={2}>
          <Grid item xs={12} md={6}>
            <ControlledField
              control={control}
              name={`legs.${legIndex}.startLocation`}
              render={(renderProps) => (
                <LocationInputField
                  {...renderProps}
                  googleConfig={googleConfig}
                  label={
                    !isDropOffDepot
                      ? getLabel()
                      : t('MultiStop.labels.multistopEndLocation').toString()
                  }
                  fieldProps={{
                    disabled: !isOrderEditAvailable || !isPickupDepot,
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ControlledField
              control={control}
              name={`legs.${legIndex}.startTime`}
              render={(renderProps) => (
                <DateTimePickerInputField
                  {...renderProps}
                  label={t('MultiStop.labels.startDate').toString()}
                  fieldProps={{
                    disabled: true,
                    format: getDateTimeShortFormatWithLocale(i18n.language),
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
        {typeof onRemoveRow === 'function' && (
          <RemoveRowButton
            isHidden={!isOrderEditAvailable || !isPickupDepot || !leg.startTime}
            onClick={() => {
              if (isPickupDepot) onRemoveRow(legIndex);
            }}
          />
        )}
      </Grid>
      {showLastLeg && (
        <>
          <Grid container item spacing={1} sx={{ position: 'relative' }}>
            {getCurrentLegProgress(true)}
            <Grid container item xs={11} rowGap={1} spacing={2}>
              <Grid item xs={12} md={6}>
                <ControlledField
                  control={control}
                  name={`legs.${legIndex}.endLocation`}
                  render={(renderProps) => (
                    <LocationInputField
                      {...renderProps}
                      googleConfig={googleConfig}
                      label={getLabel(true)}
                      fieldProps={{
                        disabled: !isOrderEditAvailable || !isDropOffDepot,
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <ControlledField
                  control={control}
                  name={`legs.${legIndex}.endTime`}
                  render={(renderProps) => (
                    <DateTimePickerInputField
                      {...renderProps}
                      label={t('MultiStop.labels.startDate').toString()}
                      fieldProps={{
                        disabled: true,
                        format: getDateTimeShortFormatWithLocale(i18n.language),
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
            {typeof onRemoveRow === 'function' && (
              <RemoveRowButton
                isHidden={!isOrderEditAvailable || !isDropOffDepot || !leg.endTime}
                onClick={() => {
                  if (isDropOffDepot) onRemoveRow(legIndex);
                }}
              />
            )}
          </Grid>
          {isOrderEditAvailable &&
            isLastLeg &&
            !isDropOffDepot &&
            typeof onAddRow === 'function' && (
              <AddRowButton onClick={() => onAddRow(legs.length)}>
                {t('MultiStop.labels.addEmptyRunDepotLocation')}
              </AddRowButton>
            )}
        </>
      )}
    </Grid>
  );
}

export default RideInfoItem;
